import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../api/axios";
import { toast } from 'react-toastify';
import { setContactInviteStatus } from "../LIAuthSlice/liAuthSlice";
import { clientDataCache, getCurrentUserIdFromStorage } from '../../utils/clientDataCache';

const taskKeys = ['linkedinDone', 'firstEmailDone', 'dmDone', 'followupEmailDone'];

/** Server/DB legacy rows may store a task slot as literal `true` instead of `{ done }`. */
const isTaskMarkedDone = (task) =>
    task === true || (!!task && typeof task === 'object' && task.done === true);

const normalizeContactIdForList = (id) => (id != null ? String(id) : null);

/**
 * Sprint-scoped completion key. One key = one completed step for one contact
 * in one sprint, so the same contact can be scheduled in several sprints (or
 * several steps of one sprint) without completions bleeding into each other.
 * Legacy progress rows (written before sprint scoping) have no sprintId and
 * are represented only in the four per-slot contactId lists below.
 */
export const makeDoneKey = (sprintId, stepIndex, contactId) =>
    `${String(sprintId)}::${String(stepIndex)}::${String(contactId)}`;

const hydrateDoneListsFromTimeline = (timeline = []) => {
    const li = new Set();
    const fe = new Set();
    const dm = new Set();
    const fu = new Set();
    const keys = new Set();

    for (let i = 0; i < timeline.length; i += 1) {
        const entry = timeline[i] || {};
        const cid = entry.contactId;
        if (!cid) continue;
        const tasks = entry.tasks || {};
        const anyDone =
            isTaskMarkedDone(tasks.linkedinDone) ||
            isTaskMarkedDone(tasks.firstEmailDone) ||
            isTaskMarkedDone(tasks.dmDone) ||
            isTaskMarkedDone(tasks.followupEmailDone);
        if (!anyDone) continue;

        // Sprint-scoped rows live ONLY in doneKeys. Feeding them into the
        // per-slot contactId lists too would let one sprint step complete
        // another via the legacy fallback — the exact bug the scoping fixes.
        if (entry.sprintId != null && entry.stepIndex != null) {
            keys.add(makeDoneKey(entry.sprintId, entry.stepIndex, cid));
            continue;
        }

        if (isTaskMarkedDone(tasks.linkedinDone)) li.add(String(cid));
        if (isTaskMarkedDone(tasks.firstEmailDone)) fe.add(String(cid));
        if (isTaskMarkedDone(tasks.dmDone)) dm.add(String(cid));
        if (isTaskMarkedDone(tasks.followupEmailDone)) fu.add(String(cid));
    }

    return {
        linkedinDone: [...li],
        firstEmailDone: [...fe],
        dmDone: [...dm],
        followupEmailDone: [...fu],
        doneKeys: [...keys]
    };
};

const applyTimelinePayload = (state, timeline) => {
    const arr = Array.isArray(timeline) ? timeline : [];
    state.taskTimeline = arr;
    state.taskHistoryByContact = buildHistoryByContact(arr);
    const lists = hydrateDoneListsFromTimeline(arr);
    state.linkedinDone = lists.linkedinDone;
    state.firstEmailDone = lists.firstEmailDone;
    state.dmDone = lists.dmDone;
    state.followupEmailDone = lists.followupEmailDone;
    state.doneKeys = lists.doneKeys;
};

const LEGACY_LIST_BY_TASK_KEY = {
    linkedinDone: 'linkedinDone',
    firstEmailDone: 'firstEmailDone',
    dmDone: 'dmDone',
    followupEmailDone: 'followupEmailDone'
};

/**
 * Apply one fulfilled mark-done result to the slice. Sprint-scoped marks land
 * ONLY in doneKeys; unscoped marks (Networking page, legacy callers) land in
 * the per-slot contactId list — mirroring hydrateDoneListsFromTimeline.
 */
const applyMarkDoneResult = (state, payload, fallbackTaskKey) => {
    const contactId = normalizeContactIdForList(payload?.contactId);
    if (!contactId) return;

    const sprintId = payload?.sprintId ?? payload?.progress?.sprintId ?? null;
    const stepIndex = payload?.stepIndex ?? payload?.progress?.stepIndex ?? null;

    if (sprintId != null && stepIndex != null) {
        const key = makeDoneKey(sprintId, stepIndex, contactId);
        if (!state.doneKeys.includes(key)) state.doneKeys.push(key);
    } else {
        const taskKey = LEGACY_LIST_BY_TASK_KEY[payload?.taskKey] || fallbackTaskKey;
        if (taskKey && !state[taskKey].includes(contactId)) {
            state[taskKey].push(contactId);
        }
    }

    if (payload?.progress) {
        applyProgressToHistory(state.taskHistoryByContact, payload.progress);
    }
};

const buildHistoryByContact = (timeline = []) => {
    const next = {};

    for (let i = 0; i < timeline.length; i += 1) {
        const entry = timeline[i] || {};
        const contactId = entry.contactId;
        if (!contactId) continue;

        if (!next[contactId]) {
            next[contactId] = {};
        }

        const tasks = entry.tasks || {};
        for (let j = 0; j < taskKeys.length; j += 1) {
            const key = taskKeys[j];
            const task = tasks[key];
            if (!isTaskMarkedDone(task)) continue;

            const t = task === true ? {} : task;
            next[contactId][key] = {
                done: true,
                doneAt: t.doneAt || entry.doneAt || null,
                completedDay: t.completedDay || entry.day || null,
                actionTaken: t.actionTaken || '',
                repliedAt: t.repliedAt || null,
                replyText: t.replyText || ''
            };
        }
    }

    return next;
};

const applyProgressToHistory = (historyByContact, progress) => {
    const contactId = progress?.contactId;
    if (!contactId) return;

    const tasks = progress?.tasks || {};
    if (!historyByContact[contactId]) {
        historyByContact[contactId] = {};
    }

    for (let i = 0; i < taskKeys.length; i += 1) {
        const key = taskKeys[i];
        const task = tasks[key];
        if (!isTaskMarkedDone(task)) continue;

        const t = task === true ? {} : task;
        historyByContact[contactId][key] = {
            done: true,
            doneAt: t.doneAt || progress?.doneAt || null,
            completedDay: t.completedDay || progress?.day || null,
            actionTaken: t.actionTaken || '',
            repliedAt: t.repliedAt || null,
            replyText: t.replyText || ''
        };
    }
};

export const sendEmail = createAsyncThunk("dailyTasks/sendEmail", async (emailData, thunkAPI) => {
    try {
        const response = await api.post("/api/gmail/sendemail", emailData);
        toast.success(response.data.msg || "Email sent successfully!");
        return response.data;
    } catch (err) {
        const errorMsg = err.response?.data?.msg || "Failed to send email.";
        toast.error(errorMsg);
        return thunkAPI.rejectWithValue(errorMsg);
    }
});

export const fetchTaskTimeline = createAsyncThunk("dailytasks/fetchTaskTimeline", async (userId, thunkAPI) => {
    try {
        const response = await api.get(`/api/dailytasks/timeline/${userId}`);
        return response.data?.timeline || [];
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
});

// Persisted outreach-email status per contact (which had a first/follow-up email
// SENT, plus the prior email's content). Hydrates the "Sent" badges and Stage 4
// gating so they survive a reload, and feeds the follow-up generator.
export const fetchOutreachStatus = createAsyncThunk("dailytasks/fetchOutreachStatus", async (contactIds, thunkAPI) => {
    try {
        const qs = Array.isArray(contactIds) && contactIds.length
            ? `?contactIds=${encodeURIComponent(contactIds.join(','))}` : '';
        const response = await api.get(`/api/gmail/outreach-status${qs}`);
        return response.data?.outreach || {};
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.msg || err.message);
    }
});

// MARKING THUNKS
// The server returns the updated progress doc, which the fulfilled reducers
// apply directly — no full-timeline refetch per click.
const postMarkDone = async (url, body, thunkAPI) => {
    try {
        const response = await api.post(url, body);
        return {
            contactId: response.data?.contactId || response.data?.progress?.contactId || body.contactId,
            taskKey: body.taskKey || null,
            sprintId: response.data?.sprintId ?? body.sprintId ?? null,
            stepIndex: response.data?.stepIndex ?? body.stepIndex ?? null,
            progress: response.data?.progress || null
        };
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
};

/**
 * Sprint-aware completion with an explicit task slot. `taskKey` comes from the
 * step's channel/intent (not the day number) and `sprintId`/`stepIndex` scope
 * the record to one step of one sprint.
 */
export const markTaskDone = createAsyncThunk("dailytasks/markTaskDone",
    ({ contactId, day, actionTaken, taskKey, sprintId, stepIndex }, thunkAPI) =>
        postMarkDone("/api/dailytasks/marktaskdone", { contactId, day, actionTaken, taskKey, sprintId, stepIndex }, thunkAPI));

export const markLinkedInAsDone = createAsyncThunk("dailytasks/markLinkedInAsDone",
    ({ contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI) =>
        postMarkDone("/api/dailytasks/marklinkedinasdone", { contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI));

export const markFirstEmailAsDone = createAsyncThunk("dailytasks/markFirstEmailAsDone",
    ({ contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI) =>
        postMarkDone("/api/dailytasks/markfirstemailasdone", { contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI));

export const markDMAsDone = createAsyncThunk("dailytasks/markDMAsDone",
    ({ contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI) =>
        postMarkDone("/api/dailytasks/markdmasdone", { contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI));

export const markFollowupEmailAsDone = createAsyncThunk("dailytasks/markFollowupEmailAsDone",
    ({ contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI) =>
        postMarkDone("/api/dailytasks/markfollowupemailasdone", { contactId, day, actionTaken, sprintId, stepIndex }, thunkAPI));

export const executeConnectionTask = createAsyncThunk("dailytasks/executeConnectionTask", async ({ taskId, text, providerId, contactId, profileUrl, accountId }, thunkAPI) => {
    try {
        let response;
        // Check if taskId is a valid MongoDB ObjectId (24 char hex)
        const isRealTaskId = /^[0-9a-fA-F]{24}$/.test(taskId);
        const requestConfig = { timeout: 45000 }; // prevent UI from getting stuck on a hanging network call

        if (isRealTaskId) {
            try {
                response = await api.post(`/api/tasks/${taskId}/send-connection`, { text }, requestConfig);
            } catch (err) {
                if (err.response?.status !== 404) throw err;
                console.log("Task not found, falling back to direct connection request");
            }
        }

        // Fallback for ghost tasks or missing DB records
        if (!response) {
            if (!accountId || (!profileUrl && !providerId)) {
                throw new Error("Missing LinkedIn data (profileUrl/providerId/accountId)");
            }
            response = await api.post("/api/linkedinauth/invite-from-url", { 
                profileUrl, 
                accountId, 
                providerId, 
                contactId,
                text 
            }, requestConfig);
        }

        toast.success("Connection request sent on LinkedIn ✓");

        thunkAPI.dispatch(setContactInviteStatus({ providerId, contactId, status: 'pending' }));

        return {
            taskId,
            contactId: response.data?.contactId || response.data?.result?.contactId || contactId,
            automated: true
        };
    } catch (err) {
        const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to send connection request.";
        toast.error(errorMsg);

        thunkAPI.dispatch(setContactInviteStatus({ providerId, contactId, status: 'failed' }));

        return thunkAPI.rejectWithValue(errorMsg);
    }
});

export const executeMessageTask = createAsyncThunk("dailytasks/executeMessageTask", async ({ taskId, text, contactId, attendeeId, accountId, userId, isInMail = false, profileUrl }, thunkAPI) => {
    try {
        let response;
        const isRealTaskId = /^[0-9a-fA-F]{24}$/.test(taskId);
        const requestConfig = { timeout: 45000 }; // prevent UI from getting stuck on a hanging network call

        if (isRealTaskId) {
            try {
                response = await api.post(`/api/tasks/${taskId}/send-message`, { text, isInMail }, requestConfig);
            } catch (err) {
                if (err.response?.status !== 404) throw err;
                console.log("Task not found, falling back to direct message");
            }
        }

        // Fallback for ghost tasks
        if (!response) {
            if (!accountId || !attendeeId || !userId) {
                throw new Error("Missing LinkedIn data (attendeeId/accountId/userId)");
            }
            response = await api.post("/api/linkedinauth/start-chat", {
                accountId,
                attendeeId,
                text,
                userId,
                isInMail,
                source: 'daily_task',
                taskId,
                contactId,
                profileUrl
            }, requestConfig);
        }

        toast.success("Message sent on LinkedIn ✓");
        return {
            taskId,
            contactId: response.data?.contactId || response.data?.data?.contactId || contactId,
            automated: true
        };
    } catch (err) {
        const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message || "Failed to send message.";
        toast.error(errorMsg);
        return thunkAPI.rejectWithValue(errorMsg);
    }
});

// Stage-level completion of the networking outreach sequence, per company.
// Persisted in the DB so "Complete & go to Stage N" survives refreshes and
// devices, and the pipeline list can render progress.
export const fetchStageProgress = createAsyncThunk("dailytasks/fetchStageProgress", async (_, thunkAPI) => {
    try {
        const response = await api.get('/api/dailytasks/stage-progress');
        return response.data?.progressByCompany || {};
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
});

export const saveStageProgress = createAsyncThunk("dailytasks/saveStageProgress", async ({ companyKey, completedStages }, thunkAPI) => {
    try {
        const response = await api.post('/api/dailytasks/stage-progress', { companyKey, completedStages });
        return response.data; // { companyKey, completedStages }
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
});

// FETCHING THUNKS
export const fetchLinkedinDoneContacts = createAsyncThunk("dailytasks/fetchLinkedinDoneContacts", async (userId, thunkAPI) => {
    try {
        const response = await api.get(`/api/dailytasks/fetchlinkedindonecontacts/${userId}`);
        return response.data?.contactIds || [];
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
});

export const fetchFirstEmailDoneContacts = createAsyncThunk("dailytasks/fetchFirstEmailDoneContacts", async (userId, thunkAPI) => {
    try {
        const response = await api.get(`/api/dailytasks/fetchfirstemaildonecontacts/${userId}`);
        return response.data?.contactIds || [];
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
});

export const fetchDMDoneContacts = createAsyncThunk("dailytasks/fetchDMDoneContacts", async (userId, thunkAPI) => {
    try {
        const response = await api.get(`/api/dailytasks/fetchdmdonecontacts/${userId}`);
        return response.data?.contactIds || [];
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
});

export const fetchFollowupEmailDoneContacts = createAsyncThunk("dailytasks/fetchFollowupEmailDoneContacts", async (userId, thunkAPI) => {
    try {
        const response = await api.get(`/api/dailytasks/fetchfollowupemaildonecontacts/${userId}`);
        return response.data?.contactIds || [];
    } catch (err) {
        return thunkAPI.rejectWithValue(err.response?.data?.message || err.message);
    }
});

const dailyTaskSlice = createSlice({
    name: "dailyTasks",
    initialState: {
        linkedinDone: [],
        firstEmailDone: [],
        dmDone: [],
        followupEmailDone: [],
        doneKeys: [], // sprint-scoped completion keys: `${sprintId}::${stepIndex}::${contactId}`
        sendingEmailId: null,
        sentFirstEmails: [],
        sentFollowupEmails: [],
        outreachByContact: {}, // contactId -> { first: {sent, subject, body}, followup: {sent} }
        taskTimeline: [],
        taskHistoryByContact: {},
        stageProgressByCompany: {}, // companyKey -> [completed stage numbers]
        stageProgressLoaded: false,
        loading: false,
        error: null,
    },
    reducers: {
        clearProgress: (state) => {
            state.linkedinDone = [];
            state.firstEmailDone = [];
            state.dmDone = [];
            state.followupEmailDone = [];
            state.doneKeys = [];
            state.error = null;
            state.sendingEmailId = null;
            state.sentFirstEmails = [];
            state.sentFollowupEmails = [];
            state.taskTimeline = [];
            state.taskHistoryByContact = {};
        },
        // Marks an email as sent locally (e.g. when sent through the generation modal
        // rather than the direct sendEmail thunk), so "Sent" badges stay in sync.
        markEmailSentLocally: (state, action) => {
            const { contactId, step, subject, body } = action.payload || {};
            if (contactId == null) return;
            const prev = state.outreachByContact[contactId] || {};
            if (step === 4) {
                if (!state.sentFollowupEmails.includes(contactId)) state.sentFollowupEmails.push(contactId);
                state.outreachByContact[contactId] = { ...prev, followup: { sent: true } };
            } else {
                if (!state.sentFirstEmails.includes(contactId)) state.sentFirstEmails.push(contactId);
                // Cache the sent first email so a follow-up can reference it without a refetch.
                state.outreachByContact[contactId] = { ...prev, first: { sent: true, subject: subject || null, body: body || null } };
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Reducers for sendEmail ---
            .addCase(sendEmail.pending, (state, action) => {
                state.sendingEmailId = action.meta.arg.contactId;
                state.error = null;
            })
            .addCase(sendEmail.fulfilled, (state, action) => {
                const { contactId, step } = action.meta.arg;

                if (step === 2) {
                    if (!state.sentFirstEmails.includes(contactId)) {
                        state.sentFirstEmails.push(contactId);
                    }
                } else if (step === 4) {
                    if (!state.sentFollowupEmails.includes(contactId)) {
                        state.sentFollowupEmails.push(contactId);
                    }
                }

                state.sendingEmailId = null;
            })
            .addCase(sendEmail.rejected, (state, action) => {
                state.sendingEmailId = null;
                state.error = action.payload;
            })
            .addCase(fetchOutreachStatus.fulfilled, (state, action) => {
                const map = action.payload || {};
                state.outreachByContact = { ...state.outreachByContact, ...map };
                for (const [cid, rec] of Object.entries(map)) {
                    if (rec?.first?.sent && !state.sentFirstEmails.includes(cid)) state.sentFirstEmails.push(cid);
                    if (rec?.followup?.sent && !state.sentFollowupEmails.includes(cid)) state.sentFollowupEmails.push(cid);
                }
            })

            // MARK: unified (explicit taskKey) + legacy per-slot thunks
            .addCase(markTaskDone.fulfilled, (state, action) => {
                applyMarkDoneResult(state, action.payload, null);
            })
            .addCase(markTaskDone.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(markLinkedInAsDone.fulfilled, (state, action) => {
                applyMarkDoneResult(state, action.payload, 'linkedinDone');
            })
            .addCase(markLinkedInAsDone.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(markFirstEmailAsDone.fulfilled, (state, action) => {
                applyMarkDoneResult(state, action.payload, 'firstEmailDone');
            })
            .addCase(markFirstEmailAsDone.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(markDMAsDone.fulfilled, (state, action) => {
                applyMarkDoneResult(state, action.payload, 'dmDone');
            })
            .addCase(markDMAsDone.rejected, (state, action) => {
                state.error = action.payload;
            })
            .addCase(markFollowupEmailAsDone.fulfilled, (state, action) => {
                applyMarkDoneResult(state, action.payload, 'followupEmailDone');
            })
            .addCase(markFollowupEmailAsDone.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Execute Connection Task
            .addCase(executeConnectionTask.pending, (state) => {
                state.loading = true;
            })
            .addCase(executeConnectionTask.fulfilled, (state, action) => {
                state.loading = false;
                const contactId = normalizeContactIdForList(action.payload?.contactId);
                if (contactId && !state.linkedinDone.includes(contactId)) {
                    state.linkedinDone.push(contactId);
                }
            })
            .addCase(executeConnectionTask.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // Execute Message Task
            .addCase(executeMessageTask.pending, (state) => {
                state.loading = true;
            })
            .addCase(executeMessageTask.fulfilled, (state, action) => {
                state.loading = false;
                const contactId = normalizeContactIdForList(action.payload?.contactId);
                if (contactId && !state.dmDone.includes(contactId)) {
                    state.dmDone.push(contactId);
                }
            })
            .addCase(executeMessageTask.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })

            // FETCH: LinkedIn
            .addCase(fetchLinkedinDoneContacts.fulfilled, (state, action) => {
                state.linkedinDone = action.payload;
            })
            .addCase(fetchLinkedinDoneContacts.rejected, (state, action) => {
                state.error = action.payload;
            })

            // Networking stage progress (per company)
            .addCase(fetchStageProgress.fulfilled, (state, action) => {
                state.stageProgressByCompany = action.payload || {};
                state.stageProgressLoaded = true;
            })
            .addCase(fetchStageProgress.rejected, (state) => {
                state.stageProgressLoaded = true;
            })
            .addCase(saveStageProgress.pending, (state, action) => {
                // Optimistic — the UI reflects the completed stage immediately.
                const { companyKey, completedStages } = action.meta.arg || {};
                if (companyKey) state.stageProgressByCompany[companyKey] = completedStages || [];
            })
            .addCase(saveStageProgress.fulfilled, (state, action) => {
                const { companyKey, completedStages } = action.payload || {};
                if (companyKey) state.stageProgressByCompany[companyKey] = completedStages || [];
            })

            // FETCH: First Email
            .addCase(fetchFirstEmailDoneContacts.fulfilled, (state, action) => {
                state.firstEmailDone = action.payload;
            })
            .addCase(fetchFirstEmailDoneContacts.rejected, (state, action) => {
                state.error = action.payload;
            })

            // FETCH: First DM
            .addCase(fetchDMDoneContacts.fulfilled, (state, action) => {
                state.dmDone = action.payload;
            })
            .addCase(fetchDMDoneContacts.rejected, (state, action) => {
                state.error = action.payload;
            })

            // FETCH: Followup Email
            .addCase(fetchFollowupEmailDoneContacts.fulfilled, (state, action) => {
                state.followupEmailDone = action.payload;
            })
            .addCase(fetchFollowupEmailDoneContacts.rejected, (state, action) => {
                state.error = action.payload;
            })

            // FETCH: Timeline / History
            .addCase(fetchTaskTimeline.pending, (state) => {
                if (!Array.isArray(state.taskTimeline) || state.taskTimeline.length === 0) {
                    const uid = getCurrentUserIdFromStorage();
                    const cached = uid ? clientDataCache.readTaskTimeline(uid) : null;
                    if (cached?.length) {
                        applyTimelinePayload(state, cached);
                    }
                }
            })
            .addCase(fetchTaskTimeline.fulfilled, (state, action) => {
                applyTimelinePayload(state, action.payload);
                const uid = getCurrentUserIdFromStorage();
                if (uid && Array.isArray(action.payload) && action.payload.length) {
                    clientDataCache.writeTaskTimeline(uid, action.payload);
                }
            })
            .addCase(fetchTaskTimeline.rejected, (state, action) => {
                state.error = action.payload;
                if (!state.taskTimeline?.length) {
                    const uid = getCurrentUserIdFromStorage();
                    const cached = uid ? clientDataCache.readTaskTimeline(uid) : null;
                    if (cached?.length) {
                        applyTimelinePayload(state, cached);
                    }
                }
            });
    }
});

export const { clearProgress, markEmailSentLocally } = dailyTaskSlice.actions;
export default dailyTaskSlice.reducer;
